import React from 'react';
import { Play, Pause, Archive, Copy, Trash2, Eye, CheckSquare, Square, TrendingUp, TrendingDown, Users, DollarSign } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const FunnelTable = ({
  funnels,
  selectedFunnels,
  loading,
  onSelectFunnel,
  onSelectAll,
  onStatusChange,
  onDuplicate,
  onDelete,
  onViewDetails
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const formatPercentage = (rate) => {
    return `${(rate || 0)?.toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US')?.format(num || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'bg-green-100 text-green-800' },
      draft: { label: 'Draft', class: 'bg-gray-100 text-gray-800' },
      paused: { label: 'Paused', class: 'bg-yellow-100 text-yellow-800' },
      archived: { label: 'Archived', class: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config?.class}`}>
        {config?.label}
      </span>
    );
  };

  const getPerformanceIndicator = (rate) => {
    const numRate = parseFloat(rate) || 0;
    if (numRate >= 20) {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    } else if (numRate < 10) {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  const QuickActionButton = ({ icon: Icon, onClick, className = "", title = "" }) => {
    return (
      <button
        onClick={onClick}
        className={`p-1 rounded hover:bg-gray-100 transition-colors ${className}`}
        title={title}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  };

  if (loading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="w-12 px-6 py-3 text-left">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Funnel Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Performance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Leads
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {[...Array(5)]?.map((_, index) => (
              <tr key={index} className="animate-pulse">
                <td className="px-6 py-4">
                  <div className="w-4 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-32 h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="w-24 h-3 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-12 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-16 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-20 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="w-24 h-4 bg-gray-200 rounded"></div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-gray-200 rounded"></div>
                    <div className="w-8 h-8 bg-gray-200 rounded"></div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (!funnels || funnels?.length === 0) {
    return (
      <div className="text-center py-16 px-6">
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-5">
          <svg className="w-9 h-9 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No funnels yet</h3>
        <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
          Create your first sales funnel to start tracking leads through your conversion pipeline.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => document.querySelector('[data-create-funnel]')?.click?.()}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Your First Funnel</span>
          </button>
          <a
            href="/ai-build-marketing-funnels"
            className="inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Use AI Builder</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="w-12 px-6 py-3 text-left">
              <button
                onClick={onSelectAll}
                className="text-gray-400 hover:text-gray-600"
              >
                {selectedFunnels?.length === funnels?.length ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
              </button>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Funnel Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Performance
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Leads
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Revenue
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {funnels?.map((funnel) => (
            <tr key={funnel?.id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <button
                  onClick={() => onSelectFunnel?.(funnel?.id)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  {selectedFunnels?.includes(funnel?.id) ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                </button>
              </td>
              
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {funnel?.name || 'Unnamed Funnel'}
                    </div>
                    {funnel?.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {funnel?.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {funnel?.funnel_steps?.length || 0} steps
                    </div>
                  </div>
                </div>
              </td>

              <td className="px-6 py-4">
                {getStatusBadge(funnel?.status)}
              </td>

              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">
                    {formatPercentage(funnel?.conversion_rate)}
                  </span>
                  {getPerformanceIndicator(funnel?.conversion_rate)}
                </div>
                <div className="text-xs text-gray-500">
                  {formatNumber(funnel?.total_conversions)} / {formatNumber(funnel?.total_leads)} converted
                </div>
              </td>

              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    {formatNumber(funnel?.total_leads)}
                  </span>
                </div>
              </td>

              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(funnel?.total_revenue)}
                  </span>
                </div>
              </td>

              <td className="px-6 py-4 text-sm text-gray-500">
                {formatDate(funnel?.created_at)}
              </td>

              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  <QuickActionButton
                    icon={Eye}
                    onClick={() => onViewDetails?.(funnel?.id)}
                    title="View Details"
                    className="text-blue-600 hover:text-blue-800"
                  />
                  
                  {funnel?.status === 'active' ? (
                    <QuickActionButton
                      icon={Pause}
                      onClick={() => onStatusChange?.(funnel?.id, 'paused')}
                      title="Pause Funnel"
                      className="text-yellow-600 hover:text-yellow-800"
                    />
                  ) : funnel?.status === 'paused' ? (
                    <QuickActionButton
                      icon={Play}
                      onClick={() => onStatusChange?.(funnel?.id, 'active')}
                      title="Activate Funnel"
                      className="text-green-600 hover:text-green-800"
                    />
                  ) : null}

                  <QuickActionButton
                    icon={Copy}
                    onClick={() => onDuplicate?.(funnel?.id, funnel?.name)}
                    title="Duplicate Funnel"
                    className="text-gray-600 hover:text-gray-800"
                  />

                  {funnel?.status !== 'archived' && (
                    <QuickActionButton
                      icon={Archive}
                      onClick={() => onStatusChange?.(funnel?.id, 'archived')}
                      title="Archive Funnel"
                      className="text-orange-600 hover:text-orange-800"
                    />
                  )}

                  <QuickActionButton
                    icon={Trash2}
                    onClick={() => onDelete?.(funnel?.id)}
                    title="Delete Funnel"
                    className="text-red-600 hover:text-red-800"
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FunnelTable;